import { Row } from 'antd';
import { debounce } from 'lodash';
import { ReactNode, useState, useEffect } from 'react';

import {
  FormItemSelect,
  Collapse,
  Section,
  Title,
} from '../../../components/index';
import { useGetBollardsQuery } from '../../../services/bollardApi';
import { useGetFendersQuery } from '../../../services/fenderApi';

const { Panel } = Collapse;

type FormItemsBollardFenderProps = {
  panelHeaderTitle: (title: string) => ReactNode;
  openCollapsePanels: string | string[];
  onOpenPanel: (panel: string | string[]) => void;
};

export function FormItemsBollardFender(props: FormItemsBollardFenderProps) {
  const { panelHeaderTitle, openCollapsePanels, onOpenPanel } = props;

  const [bollardSearchTerm, setBollardSearchTerm] = useState('');
  const [fenderSearchTerm, setFenderSearchTerm] = useState('');
  const [filteredBollards, setFilteredBollards] = useState<
    Array<{ id?: number | string; tag: string }>
  >([]);
  const [filteredFenders, setFilteredFenders] = useState<
    Array<{ id?: number | string; tag: string }>
  >([]);

  const { data: fendersData, isLoading: isLoadingFendersData } =
    useGetFendersQuery();
  const { data: bollardsData, isLoading: isLoadingBollardsData } =
    useGetBollardsQuery({ dockingPlace: '' });

  // Função para verificar se todas as palavras do termo de busca estão presentes no texto
  const containsAllSearchTerms = (
    text: string,
    searchTerm: string
  ): boolean => {
    // Divide o termo de busca em palavras separadas e remove espaços vazios
    const searchTerms = searchTerm.toLowerCase().trim().split(/\s+/);
    const textLower = text.toLowerCase();

    // Verifica se todas as palavras do termo de busca estão no texto
    return searchTerms.every((term) => textLower.includes(term));
  };

  // Filtrar cabeços com base no termo de busca
  useEffect(() => {
    if (bollardsData?.results) {
      if (bollardSearchTerm && bollardSearchTerm.trim() !== '') {
        const filtered = bollardsData.results.filter((bollard) =>
          containsAllSearchTerms(bollard.tag, bollardSearchTerm)
        );
        setFilteredBollards(filtered);
      } else {
        setFilteredBollards(bollardsData.results);
      }
    } else {
      setFilteredBollards([]);
    }
  }, [bollardsData, bollardSearchTerm]);

  // Filtrar defensas com base no termo de busca
  useEffect(() => {
    if (fendersData?.results) {
      if (fenderSearchTerm && fenderSearchTerm.trim() !== '') {
        const filtered = fendersData.results.filter((fender) =>
          containsAllSearchTerms(fender.tag, fenderSearchTerm)
        );
        setFilteredFenders(filtered);
      } else {
        setFilteredFenders(fendersData.results);
      }
    } else {
      setFilteredFenders([]);
    }
  }, [fendersData, fenderSearchTerm]);

  return (
    <Section
      activeKey={openCollapsePanels}
      onChange={onOpenPanel}
      expandIconPosition="end"
    >
      <Panel
        forceRender
        header={panelHeaderTitle('Cabeços e defensas')}
        key="bollards_and_fenders"
      >
        <Row>
          <Title>PROA</Title>
        </Row>
        <Row gutter={40} align="bottom">
          <FormItemSelect
            colSpan={8}
            label="Cabeço lançante de proa"
            name={['bollard_bow_launcher', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
          <FormItemSelect
            colSpan={8}
            label="Cabeço spring de proa"
            name={['bollard_bow_spring', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
          <FormItemSelect
            colSpan={8}
            label="Cabeço través de proa"
            name={['bollard_bow_across', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
        </Row>
        <Row gutter={40} align="bottom">
          <FormItemSelect
            colSpan={8}
            label="Primeira defensa de apoio proa"
            name={['first_bow_support_fender', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setFenderSearchTerm(e), 400)}
            onClick={() => setFenderSearchTerm('')}
            notFoundContent="Não existem defensas cadastradas"
            dataList={filteredFenders}
            isLoading={isLoadingFendersData}
          />
        </Row>
        <Row>
          <Title>POPA</Title>
        </Row>
        <Row gutter={40} align="bottom">
          <FormItemSelect
            colSpan={8}
            label="Cabeço lançante de popa"
            name={['bollard_stern_launcher', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
          <FormItemSelect
            colSpan={8}
            label="Cabeço spring de popa"
            name={['bollard_stern_spring', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
          <FormItemSelect
            colSpan={8}
            label="Cabeço través de popa"
            name={['bollard_stern_across', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setBollardSearchTerm(e), 400)}
            onClick={() => setBollardSearchTerm('')}
            notFoundContent="Não existem cabeços cadastrados"
            dataList={filteredBollards}
            isLoading={isLoadingBollardsData}
          />
        </Row>
        <Row gutter={40} align="bottom">
          <FormItemSelect
            colSpan={8}
            label="Última defensa de apoio popa"
            name={['last_stern_support_fender', 'tag']}
            allowClear
            showSearch
            onSearch={debounce((e: string) => setFenderSearchTerm(e), 400)}
            onClick={() => setFenderSearchTerm('')}
            notFoundContent="Não existem defensas cadastradas"
            dataList={filteredFenders}
            isLoading={isLoadingFendersData}
          />
        </Row>
      </Panel>
    </Section>
  );
}
